import type { I18N } from '@vista-digital/i18n'
import type {
    CompletedGiftShopOrder,
    CompletedStandardOrder,
    InProgressOrder,
    Item,
} from '@vista-digital/ocapi-types/v1'
import { getCompletedOrderById, getItemById, getOrderItems, loadCompletedOrderById } from '@vista-digital/occ-domain'
import type { OrderItemLineSelection } from '@vista-digital/occ-domain/src/lib/domains/order/types/order-item-line-selection'
import { gtm } from '../../gtm.service'
import { Ecommerce, EcommerceDataLayer, EcommerceItem, EcommercePricedItem } from '../../models-gtm'

export class GiftsGtmEvents {
    public static addPaymentInfo(items: OrderItemLineSelection[], order: InProgressOrder, i18n: I18N) {
        const ecommerceData = getPaymentEcommerceModel(order, items, i18n)
        gtm.sendDataLayer(new EcommerceDataLayer('add_payment_info', ecommerceData))
    }

    public static async payment(orderId: string, i18n: I18N) {
        await loadCompletedOrderById(orderId)
        const completedGiftShopOrder = getCompletedOrderById(orderId) as CompletedGiftShopOrder | undefined
        if (!completedGiftShopOrder) return
        const ecommerceData = getPaymentEcommerceModel(completedGiftShopOrder, completedGiftShopOrder.items, i18n)
        ecommerceData.vista_transaction_id = completedGiftShopOrder.transaction?.number
        gtm.sendDataLayer(new EcommerceDataLayer('purchase', ecommerceData))
    }

    public static selectItem(gift: Item, i18n: I18N) {
        const item = this.getGiftEcommerceModel(gift, i18n)
        gtm.sendDataLayer(new EcommerceDataLayer('select_item', item))
    }

    public static addToCart(gift: OrderItemLineSelection, i18n: I18N) {
        const model = this.getGiftEcommerceModelFromCartItem(gift, i18n)
        const pricedItems = [...model.items] as EcommercePricedItem[]
        // One item that could have more than one quantity
        pricedItems[0].quantity = getOrderItems().filter(orderItem => orderItem.itemId === gift.itemId).length
        model.items = [...pricedItems]
        gtm.sendDataLayer(new EcommerceDataLayer('add_to_cart', model))
    }

    public static removeFromCart(gift: OrderItemLineSelection, i18n: I18N) {
        const model = this.getGiftEcommerceModelFromCartItem(gift, i18n)
        const pricedItems = [...model.items] as EcommercePricedItem[]
        // Removing one item
        pricedItems[0].quantity = 1
        model.items = [...pricedItems]

        gtm.sendDataLayer(new EcommerceDataLayer('remove_from_cart', model))
    }

    private static getGiftEcommerceModelFromCartItem(gift: OrderItemLineSelection, i18n: I18N): Ecommerce {
        const items = getGiftCartItems([gift], i18n)
        return new Ecommerce(items)
    }

    private static getGiftEcommerceModel(gift: Item, i18n: I18N): Ecommerce {
        const item = getGiftEcommerceItem(gift, i18n)

        return new Ecommerce([item])
    }
}

function getGiftCartItems(gifts: OrderItemLineSelection[], i18n: I18N): EcommercePricedItem[] {
    const ecommerceItems = new Array<EcommercePricedItem>()
    for (const cartLine of gifts) {
        const giftItem = getItemById(cartLine.itemId)
        if (!giftItem) continue
        const item = getGiftEcommercePricedItem(giftItem, i18n)
        item.price = cartLine.price.valueIncludingTax
        ecommerceItems.push(item)
    }
    return ecommerceItems
}

function getPaymentEcommerceModel(
    order: InProgressOrder | CompletedStandardOrder | CompletedGiftShopOrder,
    items: OrderItemLineSelection[],
    i18n: I18N
): Ecommerce {
    const giftCartItems = getGiftCartItems(items, i18n)
    const cartItemsDictionary: {
        [key: string]: EcommercePricedItem
    } = {}
    giftCartItems.forEach(cartItem => {
        const existingCartItem = cartItemsDictionary[cartItem.item_id]
        cartItemsDictionary[cartItem.item_id] = {
            ...cartItem,
            quantity: existingCartItem && existingCartItem.quantity ? existingCartItem.quantity + 1 : 1,
        }
    })
    return new Ecommerce(Object.values(cartItemsDictionary), order.totalPrice.valueIncludingTax, order.id)
}

function mapGiftItemToEcommerceItem(gift: Item, i18n: I18N) {
    return {
        item_list_name: 'Gift Store',
        item_list_id: 'gift-store',
        item_id: gift.id,
        item_name: i18n.translatable(gift.name),
        item_category: 'Gift Store',
        item_category2: 'na',
        item_category3: 'na',
        item_category4: gift.itemClassId,
        index: 'na',
    }
}

function getGiftEcommerceItem(gift: Item, i18n: I18N): EcommerceItem {
    return new EcommerceItem(mapGiftItemToEcommerceItem(gift, i18n))
}

function getGiftEcommercePricedItem(gift: Item, i18n: I18N): EcommercePricedItem {
    return new EcommercePricedItem(mapGiftItemToEcommerceItem(gift, i18n))
}
